import { useMemo } from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import { useThemeColors } from "./calculate-metadata/theme";
import React from "react";

type SlideLike = { durationInFrames: number; transitionDurationInFrames?: number };

const Step: React.FC<{
  readonly index: number;
  readonly currentStep: number;
  readonly currentStepProgress: number;
  readonly flex: number;
}> = ({ index, currentStep, currentStepProgress, flex }) => {
  const themeColors = useThemeColors();

  const outer: React.CSSProperties = useMemo(() => {
    return {
      backgroundColor:
        themeColors.editor.lineHighlightBackground ??
        themeColors.editor.rangeHighlightBackground,
      borderRadius: 6,
      overflow: "hidden",
      height: "100%",
      flex,
    };
  }, [themeColors, flex]);

  const inner: React.CSSProperties = useMemo(() => {
    return {
      height: "100%",
      backgroundColor: themeColors.icon.foreground,
      width:
        index > currentStep
          ? 0
          : index === currentStep
            ? currentStepProgress * 100 + "%"
            : "100%",
    };
  }, [themeColors.icon.foreground, index, currentStep, currentStepProgress]);

  return (
    <div style={outer}>
      <div style={inner} />
    </div>
  );
};

export function ProgressBar({ slides }: { readonly slides: SlideLike[] }) {
  const frame = useCurrentFrame();
  useVideoConfig();

  // Account for overlap: each slide's "progress" runs until its transition starts.
  // netFrames[i] = duration[i] - transitionAfter[i] (except last slide which has no following transition)
  const netFrames = slides.map((s, i) => {
    const overlap = i < slides.length - 1 ? (s.transitionDurationInFrames ?? 0) : 0;
    const net = Math.max(1, (s.durationInFrames || 0) - overlap);
    return net;
  });

  const total = netFrames.reduce((a, b) => a + b, 0) || 1;

  // Calculate cumulative start times in the overlapped (rendered) timeline
  const starts: number[] = [];
  netFrames.reduce((acc, cur, i) => {
    starts[i] = acc;
    return acc + cur;
  }, 0 as number);

  // Find current step within net boundaries
  let currentStep = starts.length - 1;
  for (let i = 0; i < starts.length; i++) {
    if (frame >= starts[i] && frame < starts[i] + netFrames[i]) {
      currentStep = i;
      break;
    }
  }

  const currentStepProgress = Math.max(
    0,
    Math.min(1, (frame - (starts[currentStep] ?? 0)) / (netFrames[currentStep] || 1)),
  );

  const container: React.CSSProperties = useMemo(() => {
    return {
      position: "absolute",
      bottom: 32,
      height: 6,
      left: 0,
      right: 0,
      display: "flex",
      gap: 12,
      zIndex: 20,
      padding: "0 24px",
    };
  }, []);

  return (
    <div style={container}>
      {slides.map((s, index) => (
        <Step
          key={index}
          currentStep={currentStep}
          currentStepProgress={currentStepProgress}
          index={index}
          flex={netFrames[index] / total}
        />
      ))}
    </div>
  );
}
